import React, { useState } from "react";
import { toast } from "react-toastify";
import { apiRequest } from "src/async/apiUtils";
import { CheckCircleIcon } from "@heroicons/react/24/outline";
import { formatCurrency } from "src/helpers";
import Accordion from "src/components/Shared/Accordions/Accordion";
import SlideOver from "src/components/Shared/SlideOver";
import Button from "src/components/Shared/Buttons/Button";
import PlanFeatures from "src/components/Site/Plans/List/PlanFeatures";

const AssignPlanSlider = ({ restrictPlanChange = false, isOpen, client, planDetails, plans, onPlanChange, onClose }) => {
  const [selectedPlanId, setSelectedPlanId] = useState(planDetails?.plan_details?._id || "");
  const [isAssigning, setIsAssigning] = useState(false);
  const [billingCycle, setBillingCycle] = useState("year");

  const handleAssignPlan = async () => {
    if (!selectedPlanId) return;

    try {
      setIsAssigning(true);

      if (!restrictPlanChange) {
        const response = await apiRequest("PUT", `/clients/${client?._id}/plan`, {
          body: { plan_id: selectedPlanId, on_hold: false, is_adjust: false },
        });
        toast.success(response?.data?.message || "Plan updated successfully");
      }

      if (onPlanChange) await onPlanChange(selectedPlanId);

      onClose();
    } catch (error) {
      // console.error("Error assigning plan:", error);
      toast.error(error.message || "Failed to assign plan");
    } finally {
      setIsAssigning(false);
    }
  };

  const handlePlanSelect = (planId) => {
    setSelectedPlanId(planId);
  };

  return (
    <SlideOver
      isOpen={isOpen}
      handleClose={onClose}
      title={planDetails ? "Change Plan" : "Choose Plan"}
      description="View, update, and modify plan details and information below."
      size="3xl"
      footerComponent={
        <div className="flex w-full justify-end gap-x-3 ">
          <Button
            version="gray"
            onClick={onClose}
            disabled={!selectedPlanId || isAssigning}>
            Cancel
          </Button>
          <Button
            version="primary"
            type="button"
            onClick={handleAssignPlan}
            disabled={!selectedPlanId || isAssigning}
            loading={isAssigning}>
            Save
          </Button>
        </div>
      }>
      <div className="flex items-center justify-center pb-3">
        <div className="flex rounded-full bg-gray-800 p-1">
          <button
            onClick={() => setBillingCycle("month")}
            className={`rounded-full px-4 py-2 text-sm font-medium transition-all ${billingCycle === "month" ? "bg-white text-gray-900 shadow-sm" : "text-gray-300 hover:text-white"}`}>
            Monthly
          </button>
          <button
            onClick={() => setBillingCycle("year")}
            className={`relative rounded-full px-4 py-2 text-sm font-medium transition-all ${billingCycle === "year" ? "bg-white text-gray-900 shadow-sm" : "text-gray-300 hover:text-white"}`}>
            Yearly
            <span className="absolute -right-2 -top-2 rounded-full bg-green-500 px-1.5 py-0.5 text-xs text-white">Save</span>
          </button>
        </div>
      </div>

      <div className="space-y-4 py-2">
        {plans?.map((plan) => {
          const isSelected = selectedPlanId === plan._id;
          const isCurrentPlan = planDetails?.plan_details?._id === plan._id;

          const pricing = plan.pricing.rates.find((rate) => rate.interval === billingCycle);
          const displayPrice = pricing ? pricing.amount - (pricing.discount_amount || 0) : 0;
          const originalPrice = pricing?.amount;
          const hasDiscount = pricing?.discount_amount > 0;

          const headerContent = () => (
            <div
              className="flex flex-1 cursor-pointer items-center justify-between"
              onClick={() => handlePlanSelect(plan._id)}>
              <div className="flex items-center gap-4">
                <div className="flex items-center">{isSelected ? <CheckCircleIcon className="h-6 w-6 text-green-500" /> : <div className="h-6 w-6 rounded-full border-2 border-gray-300"></div>}</div>

                <div>
                  <div className="flex items-center justify-center gap-3">
                    <h3 className="text-lg font-bold text-gray-900">{plan.name}</h3>
                    {isCurrentPlan && <span className="rounded-full bg-highlightColor px-2 py-1 text-xs text-white">Current</span>}
                  </div>
                </div>
              </div>

              <div className="text-right">
                {hasDiscount && billingCycle === "year" && (
                  <div className="flex items-center justify-end gap-2">
                    <span className="text-sm text-gray-400 line-through">{formatCurrency(originalPrice)}</span>
                    <span className="rounded-full bg-green-100 px-2 py-1 text-xs text-green-800">Save {pricing.discount_percentage}%</span>
                  </div>
                )}
                <div className="flex items-baseline justify-end gap-0.5">
                  <span className="text-lg font-bold text-gray-900">{formatCurrency(displayPrice)}</span>
                  <span className="text-sm text-gray-500">/ {billingCycle}</span>
                </div>
              </div>
            </div>
          );

          return (
            <Accordion
              key={plan._id}
              headerContent={headerContent}
              startOpen={isSelected}
              isCollapsible={true}
              isSelected={isSelected}>
              <div className="space-y-3 pt-2">
                <div
                  dangerouslySetInnerHTML={{ __html: plan.description }}
                  className="mt-1 text-sm text-gray-600"
                />
                <PlanFeatures features={plan?.features || {}} />
              </div>
            </Accordion>
          );
        })}
      </div>
    </SlideOver>
  );
};

export default AssignPlanSlider;
